import React, { useContext, useEffect, useMemo, useState } from 'react';
import { ThemeContext } from '../App';
import './CentralAjuda.css';
import {
  FaBell, FaClipboardCheck, FaHome, FaSatelliteDish, FaSeedling, FaTint, FaMicrochip, FaCloudSun, FaCrown, FaUsers, FaUserCircle,
  FaSearch, FaTimes, FaChevronDown, FaChevronRight, FaQuestionCircle,
  FaEnvelope, FaLightbulb
} from 'react-icons/fa';

// Base de conteúdo dos tutoriais, organizada por módulo do app
const CATEGORIAS = [
  {
    id: 'inicio',
    titulo: 'Primeiros passos',
    icone: <FaHome />,
    cor: '#147bc0',
    topicos: [
      {
        titulo: 'Como o novo ClimoraX está organizado',
        passos: [
          'Use o menu lateral para abrir os módulos de operação, monitoramento e gestão.',
          'O Dashboard apresenta somente um resumo dos módulos liberados para a sua conta.',
          'Clique no seu nome ou avatar para acessar dados pessoais, plano, segurança e documentos legais.',
          'Use o ícone de ajuda no topo para abrir orientações relacionadas ao módulo atual.'
        ]
      }
    ]
  },
  {
    id: 'caderno',
    titulo: 'Caderno de Campo',
    icone: <FaClipboardCheck />,
    cor: '#078b78',
    topicos: [
      {
        titulo: 'Como criar e acompanhar uma ordem de serviço',
        passos: [
          'Abra “Caderno de Campo” no menu lateral e selecione “Nova ordem de serviço”.',
          'Informe a propriedade, o talhão quando aplicável, a atividade, o responsável e o prazo.',
          'Acompanhe a ordem pelos filtros de hoje, atrasadas, para aprovar, concluídas e canceladas.',
          'Abra os detalhes para editar, anexar comprovações ou registrar a execução.'
        ]
      },
      {
        titulo: 'Como registrar uma despesa rápida',
        passos: [
          'No Caderno de Campo, escolha “Nova despesa”.',
          'Selecione a propriedade e, se a despesa for específica, o talhão.',
          'Informe valor, data e uma descrição objetiva.',
          'A nota ou comprovante é opcional e pode ser anexado antes de salvar.'
        ]
      },
      {
        titulo: 'Onde consultar os antigos eventos do talhão',
        passos: [
          'Os registros anteriormente cadastrados como eventos não foram apagados.',
          'Eles aparecem no histórico do Caderno de Campo como atividades concluídas.',
          'Custos, produtos, doses, observações e anexos continuam vinculados à propriedade e ao talhão de origem.'
        ],
        dica: 'Novas atividades de campo devem ser registradas como ordens de serviço para garantir responsáveis, comprovações e rastreabilidade.'
      }
    ]
  },
  {
    id: 'alertas',
    titulo: 'Central de Alertas',
    icone: <FaBell />,
    cor: '#dc5b43',
    topicos: [
      {
        titulo: 'Como acompanhar um risco operacional',
        passos: [
          'Abra “Alertas” para visualizar ocorrências climáticas, de sensores e de manejo em um único lugar.',
          'Use os filtros para localizar o tipo e a situação do alerta.',
          'Abra o alerta para conferir sua origem, propriedade e talhão relacionados.',
          'Quando for necessário agir, encaminhe o alerta para o Caderno de Campo e acompanhe a execução.'
        ]
      }
    ]
  },
  {
    id: 'propriedades',
    titulo: 'Propriedades e Talhões',
    icone: <FaSeedling />,
    cor: '#1565c0',
    topicos: [
      {
        titulo: 'Como cadastrar uma propriedade',
        passos: [
          'Abra “Central de Operações” no menu lateral.',
          'Localize a fazenda e toque no botão flutuante “Nova propriedade”.',
          'Desenhe o perímetro no mapa e confira a área calculada em hectares.',
          'Informe os dados da fazenda e salve. Ela aparecerá automaticamente em “Propriedades e Talhões”.'
        ]
      },
      {
        titulo: 'Como cadastrar um talhão',
        passos: [
          'Abra “Central de Operações” e selecione a propriedade no mapa.',
          'Escolha a ação para criar um talhão e desenhe o perímetro dentro da propriedade.',
          'Confira a área calculada, informe o nome e os dados da cultura.',
          'Salve para disponibilizar o talhão em “Propriedades e Talhões” e nos demais módulos.'
        ]
      },
      {
        titulo: 'Como registrar a fenologia da cultura',
        passos: [
          'Entre no talhão e acesse a aba "Fenologia".',
          'Informe a data de plantio da cultura.',
          'O app calcula automaticamente os estádios fenológicos (germinação, floração, maturação etc.).',
          'Acompanhe a linha do tempo para saber em qual fase a lavoura está.'
        ]
      },
      {
        titulo: 'Como registrar uma atividade realizada no talhão',
        passos: [
          'Abra o Caderno de Campo pelo menu lateral.',
          'Crie uma ordem de serviço e selecione a propriedade e o talhão desejados.',
          'Informe atividade, responsável, prazo e comprovações necessárias.',
          'Após a execução, o registro permanece no histórico com rastreabilidade completa.'
        ],
        dica: 'Os eventos cadastrados anteriormente continuam preservados e podem ser consultados no histórico do Caderno de Campo.'
      }
    ]
  },
  {
    id: 'irrigacao',
    titulo: 'Balanço Hídrico e Irrigação',
    icone: <FaTint />,
    cor: '#0ea5e9',
    topicos: [
      {
        titulo: 'O que é o balanço hídrico',
        passos: [
          'O balanço hídrico usa o método FAO-56 para estimar a água disponível no solo.',
          'Ele cruza dados de chuva, evapotranspiração e características do solo/cultura.',
          'O resultado indica se a lavoura está em déficit, equilíbrio ou excesso hídrico.'
        ],
        dica: 'Quanto mais completos os dados de solo e cultura, mais precisa é a estimativa.'
      },
      {
        titulo: 'Como acessar o manejo de irrigação',
        passos: [
          'Abra “Irrigação” no grupo Monitoramento do menu lateral.',
          'Selecione a propriedade e o talhão que deseja analisar.',
          'Se o módulo não estiver disponível, abra “Plano e utilização” para conferir os recursos da sua conta.'
        ]
      },
      {
        titulo: 'Como configurar dados de solo e cultura',
        passos: [
          'Abra “Irrigação”, selecione a área e acesse as configurações do talhão.',
          'Revise cultura, data de plantio, solo, capacidade de água disponível e sistema de irrigação.',
          'Salve as alterações e atualize o balanço hídrico para aplicar os novos parâmetros.'
        ]
      },
      {
        titulo: 'Como visualizar recomendações de irrigação',
        passos: [
          'Abra “Irrigação” e selecione a propriedade e o talhão.',
          'Confira a condição hídrica atual, o histórico e a previsão para os próximos dias.',
          'Abra a agenda de irrigação para consultar a lâmina e o momento recomendados.'
        ],
        dica: 'As recomendações são estimativas agronômicas. Confirme as condições de campo antes de executar a irrigação.'
      }
    ]
  },
  {
    id: 'sensores',
    titulo: 'Sensores IoT',
    icone: <FaMicrochip />,
    cor: '#43a047',
    topicos: [
      {
        titulo: 'Como cadastrar um sensor',
        passos: [
          'Abra “Sensores” no grupo Monitoramento e selecione “Cadastrar estação”.',
          'Informe o Device ID, o nome e o tipo de estação.',
          'Em “Local de instalação”, busque uma cidade, informe as coordenadas ou marque o ponto no mapa.',
          'Salve e confira o estado da conexão e a chegada das primeiras leituras.'
        ],
        dica: 'A estação pode monitorar qualquer local. A proximidade com uma propriedade só é necessária para usá-la nas recomendações de irrigação dessa área.'
      },
      {
        titulo: 'Como consultar o histórico de leituras',
        passos: [
          'Abra o sensor desejado na lista de sensores.',
          'Escolha o período (dia, semana, mês) para visualizar o histórico.',
          'Use os gráficos para acompanhar a evolução das leituras ao longo do tempo.'
        ]
      },
      {
        titulo: 'O sensor não está enviando dados, o que fazer?',
        passos: [
          'Confira no cartão da estação quando ocorreu a última atualização.',
          'Verifique a alimentação e a conexão de internet ou rede do equipamento.',
          'Confirme se o Device ID cadastrado corresponde ao identificador do dispositivo.',
          'Se o problema continuar, fale com o suporte e informe o nome e o Device ID da estação.'
        ]
      }
    ]
  },
  {
    id: 'clima',
    titulo: 'Previsão Climática e Alertas',
    icone: <FaCloudSun />,
    cor: '#1976d2',
    topicos: [
      {
        titulo: 'Como consultar a previsão do tempo',
        passos: [
          'Acesse "Previsão" no menu principal.',
          'Selecione a propriedade para ver a previsão localizada para a região.',
          'Consulte temperatura, chuva e outros indicadores dos próximos dias.'
        ]
      },
      {
        titulo: 'Como configurar alertas climáticos',
        passos: [
          'Acesse "Notificações" no menu principal.',
          'Ative os tipos de alerta desejados (chuva forte, geada, entre outros).',
          'Você receberá uma notificação push quando o risco for identificado.'
        ]
      }
    ]
  },
  {
    id: 'planos',
    titulo: 'Planos, Add-ons e Pagamento',
    icone: <FaCrown />,
    cor: '#f57c00',
    topicos: [
      {
        titulo: 'Diferença entre os planos ClimoraX',
        passos: [
          'Starter Trial: acesso completo de avaliação por 30 dias, sem cartão obrigatório.',
          'Essencial: operação individual com os recursos agronômicos principais.',
          'Profissional: mais propriedades, equipe, sensores e análises NDVI.',
          'Gestão: governança, permissões, auditoria e escala para equipes maiores.'
        ],
        dica: 'Clique no seu nome, abra “Plano e utilização” e escolha “Comparar planos disponíveis” para ver valores e limites atualizados.'
      },
      {
        titulo: 'Irrigação e Central de Operações',
        passos: [
          'Os recursos de irrigação e análise territorial já fazem parte da nova grade de planos.',
          'Assinaturas antigas de add-ons continuam preservadas até o fim de sua vigência.',
          'Consulte “Plano e utilização” para confirmar os recursos disponíveis em sua conta.'
        ]
      },
      {
        titulo: 'Como consultar o histórico de pagamentos',
        passos: [
          'Clique no seu nome e abra “Plano e utilização”.',
          'Toque em "Ver Histórico" para visualizar cobranças e renovações anteriores.'
        ]
      }
    ]
  },
  {
    id: 'satelite',
    titulo: 'Central de Operações',
    icone: <FaSatelliteDish />,
    cor: '#126f9e',
    topicos: [
      {
        titulo: 'Como operar propriedades e talhões pelo mapa',
        passos: [
          'Abra “Central de Operações” no menu lateral e selecione a propriedade ou o talhão diretamente no mapa.',
          'Use os botões exibidos sobre o mapa para gerar NDVI, curvas de nível, ordens de serviço ou despesas.',
          'Para imagens, escolha o período e confira a nebulosidade antes de selecionar a cena.',
          'Use os indicadores e as camadas territoriais para identificar mudanças e planejar a ação de campo.'
        ],
        dica: 'A análise remota apoia a vistoria, mas não substitui a validação presencial quando houver uma alteração relevante.'
      }
    ]
  },
  {
    id: 'conta',
    titulo: 'Conta, segurança e ajuda',
    icone: <FaUserCircle />,
    cor: '#6d4caf',
    topicos: [
      {
        titulo: 'Como acessar a Central da Conta',
        passos: [
          'Clique no seu nome ou avatar no topo da tela.',
          'Escolha dados pessoais, plano e utilização, segurança, preferências ou documentos legais.',
          'No celular, toque no avatar no canto superior direito ou abra sua conta pelo menu lateral.'
        ]
      },
      {
        titulo: 'Como revisar dispositivos autorizados',
        passos: [
          'Clique no seu nome e escolha “Segurança e dispositivos”.',
          'Confira os aparelhos associados à sua conta.',
          'Remova somente dispositivos que você não reconhece ou que não utiliza mais.'
        ]
      }
    ]
  },
  {
    id: 'compartilhamento',
    titulo: 'Compartilhamento de Propriedades',
    icone: <FaUsers />,
    cor: '#7b1fa2',
    topicos: [
      {
        titulo: 'Como compartilhar uma propriedade',
        passos: [
          'Na lista de propriedades, toque no ícone de compartilhar (apenas o proprietário pode compartilhar).',
          'Informe o e-mail da pessoa com quem deseja compartilhar.',
          'Escolha o nível de permissão e confirme o convite.'
        ]
      },
      {
        titulo: 'Diferença entre Editor e Visualizador',
        passos: [
          'Editor: pode registrar eventos, editar dados e usar os módulos normalmente.',
          'Visualizador: acessa as informações do talhão em modo somente leitura, sem poder alterar dados.'
        ]
      },
      {
        titulo: 'Como remover o acesso de alguém',
        passos: [
          'Abra o compartilhamento da propriedade novamente pelo ícone de compartilhar.',
          'Localize a pessoa na lista de acessos e remova o compartilhamento.'
        ]
      }
    ]
  }
];

export default function CentralAjuda({ onClose, initialCategory = 'inicio' }) {
  const { darkMode } = useContext(ThemeContext);
  const [busca, setBusca] = useState('');
  const [categoriaAberta, setCategoriaAberta] = useState(
    CATEGORIAS.some(category => category.id === initialCategory) ? initialCategory : CATEGORIAS[0].id
  );
  const [topicoAberto, setTopicoAberto] = useState(null);

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const termo = busca.trim().toLowerCase();

  const categoriasFiltradas = useMemo(() => {
    if (!termo) return CATEGORIAS;
    return CATEGORIAS
      .map(cat => ({
        ...cat,
        topicos: cat.topicos.filter(t =>
          t.titulo.toLowerCase().includes(termo) ||
          t.passos.some(p => p.toLowerCase().includes(termo))
        )
      }))
      .filter(cat => cat.topicos.length > 0);
  }, [termo]);

  const categoriasVisiveis = termo
    ? categoriasFiltradas
    : categoriasFiltradas.filter(cat => cat.id === categoriaAberta);

  return (
    <div className="help-overlay" onClick={onClose}>
      <section
        className={`help-center${darkMode ? ' is-dark' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="help-center-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="help-header">
          <div className="help-heading">
            <span className="help-heading__icon"><FaQuestionCircle /></span>
            <div>
              <span className="help-eyebrow">Suporte ClimoraX</span>
              <h2 id="help-center-title">Central de Ajuda</h2>
              <p>Orientações rápidas para operar cada módulo da plataforma.</p>
            </div>
          </div>
          <button type="button" className="help-close" onClick={onClose} aria-label="Fechar Central de Ajuda">
            <FaTimes />
          </button>
          <label className="help-search">
            <FaSearch aria-hidden="true" />
            <input
              value={busca}
              onChange={(event) => setBusca(event.target.value)}
              placeholder="Busque por módulo, tarefa ou dúvida"
              aria-label="Buscar na Central de Ajuda"
            />
            {busca && (
              <button type="button" onClick={() => setBusca('')} aria-label="Limpar busca">
                <FaTimes />
              </button>
            )}
          </label>
        </header>

        <div className="help-workspace">
          <nav className="help-nav" aria-label="Módulos da ajuda">
            <span className="help-nav__label">Módulos</span>
            <div className="help-nav__items">
              {CATEGORIAS.map(cat => (
                <button
                  type="button"
                  key={cat.id}
                  className={`help-nav-item${categoriaAberta === cat.id && !termo ? ' is-active' : ''}`}
                  onClick={() => {
                    setBusca('');
                    setCategoriaAberta(cat.id);
                    setTopicoAberto(null);
                  }}
                >
                  <span className="help-nav-item__icon" style={{ '--help-category-color': cat.cor }}>{cat.icone}</span>
                  <span>{cat.titulo}</span>
                  <FaChevronRight className="help-nav-item__arrow" aria-hidden="true" />
                </button>
              ))}
            </div>
          </nav>

          <main className="help-content">
            {categoriasFiltradas.length === 0 && (
              <div className="help-empty">
                <FaSearch aria-hidden="true" />
                <strong>Nenhuma orientação encontrada</strong>
                <span>Tente buscar com termos como sensor, irrigação ou propriedade.</span>
                <button type="button" onClick={() => setBusca('')}>Limpar busca</button>
              </div>
            )}

            {termo && categoriasFiltradas.length > 0 && (
              <div className="help-results-summary">
                <span>Resultados para</span>
                <strong>“{busca.trim()}”</strong>
              </div>
            )}

            {categoriasVisiveis.map(cat => (
              <section className="help-category" key={cat.id}>
                <header className="help-category__header">
                  <span className="help-category__icon" style={{ '--help-category-color': cat.cor }}>{cat.icone}</span>
                  <div>
                    <span>{termo ? 'Resultado em' : 'Guia do módulo'}</span>
                    <h3>{cat.titulo}</h3>
                  </div>
                </header>

                <div className="help-topics">
                  {cat.topicos.map((topico, index) => {
                    const chaveTopico = `${cat.id}-${index}`;
                    const aberto = termo || topicoAberto === chaveTopico;
                    return (
                      <article className={`help-topic${aberto ? ' is-open' : ''}`} key={chaveTopico}>
                        <button
                          type="button"
                          className="help-topic__trigger"
                          onClick={() => !termo && setTopicoAberto(aberto ? null : chaveTopico)}
                          aria-expanded={aberto}
                        >
                          <span className="help-topic__number">{String(index + 1).padStart(2, '0')}</span>
                          <span>{topico.titulo}</span>
                          {aberto ? <FaChevronDown aria-hidden="true" /> : <FaChevronRight aria-hidden="true" />}
                        </button>
                        {aberto && (
                          <div className="help-topic__body">
                            <ol>
                              {topico.passos.map((passo, passoIndex) => <li key={passoIndex}>{passo}</li>)}
                            </ol>
                            {topico.dica && (
                              <div className="help-tip">
                                <FaLightbulb aria-hidden="true" />
                                <div><strong>Dica prática</strong><span>{topico.dica}</span></div>
                              </div>
                            )}
                          </div>
                        )}
                      </article>
                    );
                  })}
                </div>
              </section>
            ))}
          </main>
        </div>

        <footer className="help-footer">
          <div><strong>Ainda precisa de ajuda?</strong><span>Nossa equipe pode orientar você.</span></div>
          <a href={`mailto:climorax.app@gmail.com?subject=${encodeURIComponent('Suporte ClimoraX')}&body=${encodeURIComponent('Olá,\n\nPreciso de ajuda com:\n\n')}`}>
            <FaEnvelope /> Falar com o suporte
          </a>
        </footer>
      </section>
    </div>
  );
}
